import React from "react";
import { Link } from "react-router-dom";

import { DATA_PROCESSORS, LEGAL_CONFIG, PUBLIC_RESULT_SOURCES } from "./legal-config";
import { LegalPage, LegalSection } from "./legal-page";

const PrivacyPolicy: React.FC = () => {
  return (
    <LegalPage
      title="Polityka prywatności"
      lead={`Jak serwis ${LEGAL_CONFIG.serviceName} zbiera, wykorzystuje i chroni dane osobowe zawodników, organizatorów i użytkowników konta.`}
    >
      <LegalSection id="administrator" number="1" title="Kto odpowiada za Twoje dane">
        <p>
          Administratorem danych osobowych przetwarzanych w serwisie{" "}
          {LEGAL_CONFIG.serviceName}, dostępnym pod adresami{" "}
          {LEGAL_CONFIG.serviceDomains.join(" i ")}, jest{" "}
          <strong>{LEGAL_CONFIG.administratorName}</strong>,{" "}
          {LEGAL_CONFIG.administratorAddress}. Serwis prowadzony jest niezarobkowo, przez
          osobę fizyczną nieprowadzącą działalności gospodarczej — korzystanie z niego
          jest bezpłatne i nie pobieramy żadnych opłat.
        </p>
        <p>
          We wszystkich sprawach dotyczących danych osobowych — w tym w celu skorzystania
          z praw opisanych w punkcie 8 — napisz na adres{" "}
          <strong>{LEGAL_CONFIG.contactEmail}</strong>. Nie wyznaczono inspektora ochrony
          danych; korespondencję obsługuje bezpośrednio administrator.
        </p>
        <h3>1.1. Rola klubów i organizatorów</h3>
        <p>
          Ranking, bazę zawodników i historię meczów prowadzimy samodzielnie — o tym, jak
          liczony jest rating, co jest publikowane i jak długo, decyduje wyłącznie
          administrator serwisu.
        </p>
        <p>
          Inaczej jest z zapisami na kolejki. Klub decyduje, kogo dopuszcza do swoich
          rozgrywek i kiedy kontaktuje się z uczestnikami; my dostarczamy formularz,
          przechowujemy dane na naszym serwerze, ustalamy okresy ich usuwania i
          wykorzystujemy wyniki meczów do rankingu. Ponieważ o celach i środkach decydujemy
          wspólnie, w tym zakresie jesteśmy{" "}
          <strong>współadministratorami w rozumieniu art. 26 RODO</strong>.
        </p>
        <p>
          Podział obowiązków: administrator serwisu odpowiada za bezpieczeństwo systemu,
          okresy przechowywania, publikację wyników oraz obsługę wszystkich żądań z art.
          15–21 RODO; organizator — za decyzje o dopuszczeniu do rozgrywek i za treść
          kontaktu z uczestnikami. Niezależnie od tego podziału{" "}
          <strong>każde żądanie możesz skierować do nas</strong>, na adres{" "}
          {LEGAL_CONFIG.contactEmail}.
        </p>
      </LegalSection>

      <LegalSection id="dane" number="2" title="Jakie dane przetwarzamy">
        <p>Dane trafiają do serwisu trzema drogami.</p>

        <h3>2.1. Dane, które podajesz sam</h3>
        <table className="legal-table">
          <thead>
            <tr>
              <th>Sytuacja</th>
              <th>Zakres danych</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td data-label="Sytuacja">Założenie konta i logowanie</td>
              <td data-label="Zakres danych">
                adres e-mail albo login nadany przez organizatora — pełni rolę
                identyfikatora konta; hasło (przechowywane wyłącznie w postaci skrótu
                kryptograficznego), rola w serwisie, powiązanie z klubem i z profilem
                zawodnika, data utworzenia konta
              </td>
            </tr>
            <tr>
              <td data-label="Sytuacja">Utrzymanie zalogowania</td>
              <td data-label="Zakres danych">
                identyfikator konta, token sesji oraz data jego utworzenia i wygaśnięcia —
                przechowywane również po stronie serwera, żeby wylogowanie natychmiast
                unieważniało sesję
              </td>
            </tr>
            <tr>
              <td data-label="Sytuacja">
                Zapis na kolejkę ligi (także bez konta, przez link organizatora)
              </td>
              <td data-label="Zakres danych">
                imię i nazwisko, adres e-mail i numer telefonu, imię i nazwisko partnera
                w deblu, status zapisu
              </td>
            </tr>
            <tr>
              <td data-label="Sytuacja">Pula „szukam partnera”</td>
              <td data-label="Zakres danych">
                identyfikator zawodnika, treść notatki, którą sam wpisujesz, oraz treść
                zgłoszeń od innych zawodników. <strong>Notatka i zgłoszenia są jawne</strong>{" "}
                — widzi je każdy, kto otworzy stronę tej kolejki
              </td>
            </tr>
            <tr>
              <td data-label="Sytuacja">Wpisywanie wyników przez link do arkusza</td>
              <td data-label="Zakres danych">
                wyniki setów i meczów przypisane do konkretnych zawodników
              </td>
            </tr>
          </tbody>
        </table>

        <h3>2.1a. Dane, które wprowadza o Tobie organizator</h3>
        <p>
          Część danych trafia do serwisu nie od Ciebie, lecz od organizatora ligi —
          przy zatwierdzaniu zapisu albo przy zakładaniu Twojego profilu zawodnika. Są to:
        </p>
        <ul>
          <li>
            <strong>poziom gry</strong> — ocenia go organizator; służy do wyliczenia
            ratingu startowego i do przydziału do grupy,
          </li>
          <li>
            <strong>płeć</strong> — potrzebna do kategorii rozgrywek i klasyfikacji,
          </li>
          <li>
            <strong>nazwa wyświetlana</strong> zawodnika oraz przypisanie do klubu i ligi.
          </li>
        </ul>
        <p>
          Jeżeli zapisując się na kolejkę podajesz imię i nazwisko partnera do debla,
          podajesz nam dane innej osoby — upewnij się, że ta osoba wie o zapisaniu jej na
          kolejkę.
        </p>

        <h3>2.2. Wyniki turniejowe pozyskane ze źródeł publicznych</h3>
        <p>
          Ranking prowadzony w serwisie opiera się na wynikach meczów publikowanych przez
          organizatorów lig i turniejów. Pobieramy je z ogólnodostępnych stron:
        </p>
        <ul>
          {PUBLIC_RESULT_SOURCES.map((source) => (
            <li key={source.name}>
              <a href={source.url} target="_blank" rel="noreferrer noopener">
                {source.name}
              </a>
            </li>
          ))}
        </ul>
        <p>
          Z tych źródeł pobieramy imię i nazwisko zawodnika, datę i nazwę turnieju oraz
          wyniki poszczególnych meczów wraz ze składami par. Na tej podstawie serwis
          wylicza rating ELO, historię startów i statystyki. Do serwisu{" "}
          <strong>nie przenosimy</strong> rocznika, numeru licencji, przynależności
          klubowej ani identyfikatorów zawodnika z systemów źródłowych.
        </p>
        <p>
          Na potrzeby weryfikacji poprawności obliczeń przechowujemy kopie stron
          źródłowych w takiej postaci, w jakiej zostały opublikowane. Kopie te nie są
          nikomu udostępniane ani publikowane.
        </p>
        <p>
          Ta część danych nie pochodzi od Ciebie, dlatego niniejsza polityka pełni
          jednocześnie rolę informacji wymaganej przez art. 14 RODO. Wyniki pobieramy na
          bieżąco, w miarę jak organizatorzy publikują kolejne kolejki i turnieje — zwykle
          w ciągu kilku dni od rozegrania. Jeżeli Twoje wyniki trafiły do serwisu, zanim ta
          strona powstała, informacja dociera do Ciebie z opóźnieniem wobec terminu z art.
          14 ust. 3 RODO — odnotowujemy to wprost, a każde nowe źródło zgłaszamy tu
          niezwłocznie, nie później niż w ciągu miesiąca od pierwszego pobrania z niego
          danych.
        </p>
        <p>
          Nie jesteśmy w stanie zawiadomić indywidualnie każdego zawodnika, którego wynik
          trafił do rankingu. Ze stron organizatorów pobieramy wyłącznie imię, nazwisko
          i wynik meczu —{" "}
          <strong>
            nie pobieramy i nie posiadamy żadnych adresów e-mail ani numerów telefonu tych
            osób
          </strong>
          . Żeby wysłać indywidualne zawiadomienie, musielibyśmy najpierw zgromadzić dane
          kontaktowe kilku tysięcy osób, a więc zebrać o nich <em>więcej</em> danych, niż
          faktycznie przetwarzamy, i to wyłącznie po to, by poinformować je
          o przetwarzaniu. Byłoby to działanie odwrotne do celu przepisu i wymagałoby
          niewspółmiernie dużego wysiłku w rozumieniu art. 14 ust. 5 lit. b RODO. Dlatego —
          tak jak przewiduje ten przepis — informację udostępniamy publicznie: ta strona
          jest dostępna bez logowania, z odnośnika w stopce każdej podstrony serwisu.
        </p>
        <p>
          Dane objęte tym wyjątkiem są wyłącznie danymi zwykłymi, pochodzącymi
          z materiałów, które organizatorzy sami opublikowali w otwartym internecie — nie
          przetwarzamy tu żadnych danych szczególnych kategorii.
        </p>

        <h3>2.3. Dane techniczne</h3>
        <p>
          Serwery, na których działa serwis, prowadzą standardowe logi: adres IP, datę
          i godzinę zapytania, adres żądanego zasobu, kod odpowiedzi oraz informacje
          o przeglądarce. Logi służą utrzymaniu serwisu i wykrywaniu nadużyć. Nie
          używamy narzędzi analitycznych, reklamowych ani żadnych mechanizmów śledzenia
          — szczegóły w{" "}
          <Link to="/cookies">informacji o plikach cookie i pamięci przeglądarki</Link>.
        </p>
      </LegalSection>

      <LegalSection id="cele" number="3" title="Po co i na jakiej podstawie prawnej">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Cel</th>
              <th>Podstawa prawna</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td data-label="Cel">
                Prowadzenie konta, logowanie, obsługa zapisów na kolejki i turnieje,
                dobieranie par
              </td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. b RODO — wykonanie umowy o świadczenie usługi drogą
                elektroniczną, którą zawierasz korzystając z tych funkcji; jej treść
                określa <Link to="/regulamin">regulamin</Link>
              </td>
            </tr>
            <tr>
              <td data-label="Cel">
                Prowadzenie i publikowanie rankingu ELO, wyników meczów, klasyfikacji
                sezonowych i historii startów
              </td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes polegający na
                prowadzeniu jawnego, weryfikowalnego rankingu sportowego dla lokalnego
                środowiska badmintona, opartego na wynikach, które organizatorzy publikują
                publicznie
              </td>
            </tr>
            <tr>
              <td data-label="Cel">
                Kontakt organizatora z osobą zapisaną na kolejkę (potwierdzenie miejsca,
                zmiana terminu, odwołanie gry)
              </td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. b RODO — gdy podanie kontaktu było warunkiem zapisu,
                przetwarzanie jest niezbędne do zorganizowania kolejki, na którą się
                zapisałeś. Gdy podałeś e-mail lub telefon mimo że zapis był możliwy bez
                nich — art. 6 ust. 1 lit. f RODO, a uzasadnionym interesem jest możliwość
                powiadomienia Cię o nagłej zmianie terminu lub odwołaniu gry, żebyś nie
                przyjechał na halę na próżno
              </td>
            </tr>
            <tr>
              <td data-label="Cel">
                Prowadzenie puli „szukam partnera”: publikacja Twojego zgłoszenia wraz
                z notatką i pośredniczenie w kontakcie z zawodnikami, którzy się odezwą
              </td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. b RODO — wykonanie usługi kojarzenia partnerów do
                debla, o którą sam poprosiłeś, publikując zgłoszenie
              </td>
            </tr>
            <tr>
              <td data-label="Cel">Zapisanie płci zawodnika</td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes polegający na
                przypisaniu zawodnika do właściwej kategorii rozgrywek (gra pojedyncza
                kobiet, mężczyzn, mikst) i na prawidłowym wyliczeniu klasyfikacji
              </td>
            </tr>
            <tr>
              <td data-label="Cel">
                Bezpieczeństwo serwisu, diagnostyka błędów, przeciwdziałanie nadużyciom
                i nieuprawnionemu dostępowi
              </td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes polegający na
                utrzymaniu integralności i dostępności serwisu, wykrywaniu prób przejęcia
                cudzych kont, blokowaniu automatycznych nadużyć oraz na ochronie danych
                zapisanych przez pozostałych użytkowników
              </td>
            </tr>
            <tr>
              <td data-label="Cel">
                Prowadzenie logów serwera (adres IP, data i godzina, adres zasobu, kod
                odpowiedzi, informacje o przeglądarce)
              </td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes polegający na
                utrzymaniu ciągłości działania serwisu, diagnozowaniu awarii oraz
                wykrywaniu prób nieuprawnionego dostępu
              </td>
            </tr>
            <tr>
              <td data-label="Cel">Tworzenie i przechowywanie kopii zapasowych bazy danych</td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes polegający na
                możliwości odtworzenia serwisu po awarii sprzętu lub błędzie
                oprogramowania, co chroni także dorobek sportowy samych zawodników
              </td>
            </tr>
            <tr>
              <td data-label="Cel">Ustalenie, dochodzenie lub obrona przed roszczeniami</td>
              <td data-label="Podstawa prawna">
                art. 6 ust. 1 lit. f RODO — prawnie uzasadniony interes polegający na
                możliwości wykazania, kto i kiedy zapisał się na kolejkę oraz jaki wynik
                został zgłoszony, gdyby doszło do sporu o przebieg rozgrywek, o wysokość
                ratingu albo do zarzutu naruszenia dóbr osobistych w związku z publikacją
                wyniku
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          Wszędzie tam, gdzie powołujemy się na prawnie uzasadniony interes,
          przeprowadziliśmy ocenę, czy Twoje prawa i wolności nie przeważają nad tym
          interesem. Na Twoje żądanie przekażemy Ci podsumowanie tej oceny — napisz na{" "}
          <strong>{LEGAL_CONFIG.contactEmail}</strong>.
        </p>
        <p>
          Nie przetwarzamy danych w celach marketingowych i nie wysyłamy informacji
          handlowych. Nie sprzedajemy ani nie udostępniamy danych podmiotom
          reklamowym.
        </p>
        <p>
          Żadne z opisanych wyżej przetwarzań{" "}
          <strong>nie opiera się na Twojej zgodzie</strong> — dlatego nie prosimy Cię o nią
          i nie ma tu czego cofać. Gdybyśmy w przyszłości chcieli przetwarzać dane w celu,
          który zgody wymaga (na przykład statystyki odwiedzin), poprosimy o nią osobno,
          a Ty będziesz mógł ją wycofać w każdej chwili; wycofanie nie wpłynie na
          zgodność z prawem tego, co zrobiliśmy wcześniej.
        </p>
        <p>
          Jeżeli kiedykolwiek zamierzalibyśmy przetwarzać Twoje dane w celu innym niż
          opisany powyżej, poinformujemy Cię o tym celu i podamy jego podstawę prawną,
          zanim zaczniemy to robić.
        </p>
      </LegalSection>

      <LegalSection id="jawnosc" number="4" title="Co jest widoczne publicznie">
        <p>
          Serwis z założenia jest jawny. Bez logowania każdy może zobaczyć: nazwę
          zawodnika, jego rating ELO i historię jego zmian, rozegrane mecze wraz
          z wynikami, partnerów i przeciwników, przynależność do klubu lub ligi,
          klasyfikacje sezonowe, listy zapisów na kolejki oraz{" "}
          <strong>treść notatek i zgłoszeń w puli „szukam partnera”</strong>.
        </p>
        <p>
          Notatka w puli to pole wolnego tekstu — wpisujesz w nie, co chcesz, a widzi je
          każdy odwiedzający. Nie umieszczaj tam numeru telefonu ani adresu, jeżeli nie
          chcesz, żeby były publiczne.
        </p>
        <p>
          <strong>Nie są publikowane</strong>: adres e-mail, numer telefonu, hasło ani
          adres IP. Dane kontaktowe podane przy zapisie widzi wyłącznie organizator danej
          ligi oraz administrator serwisu.
        </p>
      </LegalSection>

      <LegalSection id="odbiorcy" number="5" title="Komu udostępniamy dane">
        <ul>
          <li>
            <strong>Wszyscy odwiedzający serwis</strong> — dane wymienione w punkcie 4 są
            dostępne publicznie w internecie, bez logowania. Odbiorcą tych danych może
            być każdy, a wyszukiwarki internetowe mogą je zaindeksować. Nie dotyczy to
            danych kontaktowych ani danych konta.
          </li>
          {DATA_PROCESSORS.map((processor) => (
            <li key={processor.role}>
              <strong>{processor.role}</strong> — {processor.name}; {processor.scope}.
            </li>
          ))}
          <li>
            <strong>Organizatorzy klubów i lig</strong> — w zakresie zapisów na
            prowadzone przez nich wydarzenia (imię i nazwisko, status zapisu, wyniki).
            Dane kontaktowe widzą tylko do momentu rozliczenia kolejki, o czym mowa
            w punkcie 6.
          </li>
          <li>
            <strong>Uprawnione organy</strong> — jeżeli obowiązek udostępnienia wynika
            z przepisów prawa.
          </li>
        </ul>
        <p>
          Dane nie są przekazywane poza Europejski Obszar Gospodarczy ani do organizacji
          międzynarodowych. Serwis nie ładuje żadnych zasobów z serwerów zewnętrznych —
          nawet kroje pism hostujemy u siebie — więc Twój adres IP nie trafia do żadnego
          podmiotu poza wymienionych wyżej dostawców infrastruktury.
        </p>
      </LegalSection>

      <LegalSection id="okres" number="6" title="Jak długo przechowujemy dane">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Kategoria</th>
              <th>Okres</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td data-label="Kategoria">Konto użytkownika</td>
              <td data-label="Okres">
                do czasu usunięcia konta na Twoje żądanie zgłoszone na adres kontaktowy
              </td>
            </tr>
            <tr>
              <td data-label="Kategoria">Sesja logowania</td>
              <td data-label="Okres">
                token traci ważność po 7 dniach od zalogowania albo z chwilą wylogowania
              </td>
            </tr>
            <tr>
              <td data-label="Kategoria">
                Dane kontaktowe podane przy zapisie na kolejkę oraz treść notatek
                i zgłoszeń w puli „szukam partnera”
              </td>
              <td data-label="Okres">
                do rozliczenia tej kolejki — w chwili, gdy kolejka zostaje zamknięta
                i wyniki trafiają do rankingu, adres e-mail, numer telefonu oraz treść
                notatek i zgłoszeń są automatycznie kasowane; zapis i nazwisko zostają,
                kontakt znika
              </td>
            </tr>
            <tr>
              <td data-label="Kategoria">Wyniki meczów, rating i historia startów</td>
              <td data-label="Okres">
                bezterminowo — ranking jest historyczny i przeliczany od pierwszego
                zarejestrowanego meczu; usunięcie pojedynczego wyniku zmieniłoby rating
                wszystkich pozostałych uczestników tego meczu. Nie rzadziej niż raz na
                trzy lata sprawdzamy, czy przechowywanie całej historii jest nadal
                potrzebne do prowadzenia rankingu
              </td>
            </tr>
            <tr>
              <td data-label="Kategoria">Kopie zapasowe bazy danych</td>
              <td data-label="Okres">
                przechowujemy 14 ostatnich kopii; każda kolejna aktualizacja serwisu
                usuwa najstarszą. Kopia wykonana przed skasowaniem danych kontaktowych
                zachowuje je do momentu, aż wypadnie z tej rotacji
              </td>
            </tr>
            <tr>
              <td data-label="Kategoria">Logi serwera</td>
              <td data-label="Okres">
                nie dłużej niż 12 miesięcy
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          Kasowanie danych kontaktowych po kolejce jest automatyczne i nieodwracalne —
          dzieje się w tej samej operacji, w której wyniki wchodzą do rankingu, i obejmuje
          również wewnętrzny dziennik zdarzeń serwisu. Dotyczy to obu sposobów zamknięcia
          kolejki: zamknięcia jej przez organizatora w panelu i wczytania pliku
          z wynikami.
        </p>
        <p>
          Dopóki kolejka nie zostanie rozliczona, dane kontaktowe pozostają w serwisie —
          jeżeli organizator porzuci kolejkę i nigdy jej nie zamknie, możesz zażądać
          usunięcia swoich danych kontaktowych wcześniej, pisząc na adres kontaktowy.
        </p>
        <p>
          Jedynym adresem e-mail, który zostaje na dłużej, jest ten przypisany do konta,
          bo służy do logowania; usuwasz go, usuwając konto.
        </p>
      </LegalSection>

      <LegalSection id="bezpieczenstwo" number="7" title="Jak chronimy dane">
        <ul>
          <li>
            Połączenie między Twoją przeglądarką a serwisem jest szyfrowane (HTTPS),
            a wejście po nieszyfrowanym adresie jest automatycznie przekierowywane.
          </li>
          <li>
            Hasła przechowujemy wyłącznie w postaci skrótu kryptograficznego (scrypt
            z losową solą) — nikt, łącznie z administratorem, nie ma dostępu do hasła
            w jawnej postaci.
          </li>
          <li>
            Sesja logowania opiera się na tokenie o ograniczonej ważności (7 dni),
            zapisywanym w pamięci Twojej przeglądarki, nie w pliku cookie. Jego
            odpowiednik przechowujemy też po stronie serwera, żeby wylogowanie
            natychmiast unieważniało sesję.
          </li>
          <li>
            Dane kontaktowe uczestników kolejki widzi wyłącznie zalogowany organizator
            klubu, który tę kolejkę prowadzi, oraz administrator serwisu.
          </li>
          <li>
            Przed każdą aktualizacją serwisu wykonujemy kopię bazy danych na potrzeby
            odtworzenia po awarii.
          </li>
        </ul>
        <p>
          Serwis nie wysyła żadnych wiadomości e-mail. Adres podany przy rejestracji nie
          jest weryfikowany i służy wyłącznie jako login; odzyskanie dostępu do konta oraz
          jego usunięcie realizuje administrator po zgłoszeniu na adres kontaktowy.
        </p>
      </LegalSection>

      <LegalSection id="prawa" number="8" title="Twoje prawa">
        <p className="legal-callout">
          <strong>Prawo sprzeciwu.</strong> Jeżeli Twoje dane przetwarzamy na podstawie
          prawnie uzasadnionego interesu — a dotyczy to całego rankingu, wyników meczów
          i historii startów —{" "}
          <strong>
            masz prawo w dowolnym momencie wnieść sprzeciw wobec tego przetwarzania
            z przyczyn związanych z Twoją szczególną sytuacją
          </strong>
          . Wystarczy wiadomość na {LEGAL_CONFIG.contactEmail}. Niżej opisujemy, jak taki
          sprzeciw rozpatrujemy.
        </p>
        <p>W związku z przetwarzaniem danych przysługuje Ci ponadto prawo do:</p>
        <ul>
          <li>dostępu do danych i otrzymania ich kopii (art. 15 RODO),</li>
          <li>sprostowania danych nieprawidłowych lub niekompletnych (art. 16 RODO),</li>
          <li>usunięcia danych (art. 17 RODO),</li>
          <li>ograniczenia przetwarzania (art. 18 RODO),</li>
          <li>
            przenoszenia danych, które podałeś na podstawie umowy (art. 20 RODO).
          </li>
        </ul>
        <p>
          Serwis nie ma przycisku do samodzielnego pobrania kopii danych ani do usunięcia
          konta — wszystkie te żądania realizuje administrator ręcznie, po zgłoszeniu na
          adres kontaktowy. Prawo do ograniczenia przetwarzania (art. 18 RODO) przy tym
          modelu działania realizujemy przez zastąpienie Twojego imienia i nazwiska
          oznaczeniem pseudonimowym w widokach publicznych — opisujemy to niżej.
        </p>
        <p>
          Żądanie zgłoś na adres <strong>{LEGAL_CONFIG.contactEmail}</strong>. Odpowiadamy
          bez zbędnej zwłoki, najpóźniej w ciągu miesiąca.
        </p>
        <h3>Co dokładnie robimy po sprzeciwie wobec publikacji nazwiska</h3>
        <p>
          Dane kontaktowe i konto usuwamy na żądanie zawsze. Z samym wynikiem sportowym
          jest inaczej: wyniki meczów i turniejów są z natury jawne — publikuje je
          organizator rozgrywek, a zawodnik przystępując do ligi rozsądnie się tego
          spodziewa. Wynik dotyczy przy tym równocześnie Twoich partnerów
          i przeciwników, więc usunięcie go z historii zafałszowałoby ich rating.
          Dlatego co do zasady mamy tu ważne prawnie uzasadnione podstawy w rozumieniu
          art. 21 ust. 1 RODO i utrzymujemy publikację wyników.
        </p>
        <p>
          Każdy sprzeciw rozpatrujemy jednak indywidualnie i ważymy Twoją sytuację wobec
          naszego interesu. Jeżeli podasz szczególne okoliczności, które przeważają —
          zastępujemy Twoje imię i nazwisko w widokach publicznych oznaczeniem
          pseudonimowym (np. „Zawodnik #1234”), zachowując same mecze na potrzeby
          rankingu pozostałych zawodników. Odpowiedź wraz z uzasadnieniem otrzymasz
          niezależnie od rozstrzygnięcia.
        </p>
        <p>
          Uczciwie o granicach tego rozwiązania: jest to{" "}
          <strong>pseudonimizacja, nie anonimizacja</strong>. Zmiana obejmuje widoki
          publiczne serwisu, natomiast wewnętrzny dziennik zdarzeń oraz archiwalne pliki
          z wynikami opublikowanymi przez organizatorów zachowują pierwotne oznaczenie.
          Nie mamy też wpływu na strony organizatorów, z których wyniki pochodzą — tam
          o usunięcie trzeba wystąpić osobno.
        </p>
        <p>
          Masz również prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych
          Osobowych, ul. Stawki 2, 00-193 Warszawa.
        </p>
      </LegalSection>

      <LegalSection id="dobrowolnosc" number="9" title="Czy podanie danych jest obowiązkowe">
        <p>
          Podanie danych nie wynika z żadnego przepisu prawa — nie jest wymogiem
          ustawowym. Jest natomiast{" "}
          <strong>wymogiem umownym, a częściowo warunkiem zawarcia umowy</strong>: bez
          pewnych danych nie da się wykonać usługi, o którą prosisz.
        </p>
        <ul>
          <li>
            <strong>Adres e-mail i hasło</strong> — warunek założenia konta. Bez nich
            konta nie utworzymy, bo nie byłoby jak Cię zalogować.
          </li>
          <li>
            <strong>Imię i nazwisko</strong> — warunek zapisu na kolejkę. Bez nich
            organizator nie umieści Cię na liście ani nie przypisze Ci wyników.
          </li>
          <li>
            <strong>Adres e-mail lub numer telefonu przy zapisie</strong> — zapisując się
            z publicznego linku musisz podać co najmniej jedno z nich; bez żadnego
            formularz odmówi przyjęcia zgłoszenia. Jeżeli wchodzisz z linku zawierającego
            kod organizatora, oba pola są opcjonalne i możesz je pominąć.
          </li>
          <li>
            <strong>Płeć</strong> — warunek przypisania do kategorii rozgrywek
            rozdzielonych ze względu na płeć. Bez niej możesz grać, ale nie trafisz do
            klasyfikacji takiej kategorii.
          </li>
        </ul>
        <p>
          Przeglądanie rankingów, wyników i klasyfikacji nie wymaga podawania żadnych
          danych ani zakładania konta.
        </p>
      </LegalSection>

      <LegalSection id="profilowanie" number="10" title="Automatyczne przetwarzanie i ranking">
        <p>
          Rating ELO, przydział do grup i dobór par wyliczane są automatycznie, wyłącznie
          na podstawie wyników rozegranych meczów. Jest to{" "}
          <strong>profilowanie</strong> w rozumieniu art. 4 pkt 4 RODO, ponieważ
          automatycznie ocenia jeden Twój czynnik osobowy — poziom gry.
        </p>
        <p>
          <strong>
            Nie jest to natomiast zautomatyzowana decyzja w rozumieniu art. 22 RODO.
          </strong>{" "}
          Wyliczony rating nie wywołuje wobec Ciebie skutków prawnych ani nie wpływa na
          Ciebie w podobnie istotny sposób: nie decyduje o dostępie do żadnej usługi ani
          o żadnym uprawnieniu — jest wyłącznie klasyfikacją sportową. Decyzję
          o dopuszczeniu do rozgrywek i o przydziale do grupy podejmuje organizator,
          człowiek, który może wynik algorytmu zmienić.
        </p>
        <p>
          Zasada działania algorytmu jest jawna: wygrana z zawodnikiem o wyższym ratingu
          podnosi Twój rating bardziej niż wygrana z zawodnikiem słabszym, a wielkość
          zmiany zależy od różnicy ratingów obu stron. Pełną historię zmian własnego
          ratingu wraz z meczem, który każdą zmianę spowodował, widzisz w swoim profilu.
        </p>
        <p>
          Ponieważ profilowanie opieramy na prawnie uzasadnionym interesie,{" "}
          <strong>przysługuje Ci wobec niego sprzeciw</strong> na zasadach opisanych
          w punkcie 8.
        </p>
      </LegalSection>

      <LegalSection id="maloletni" number="11" title="Osoby niepełnoletnie">
        <p>
          Serwis <strong>nie zbiera daty urodzenia i nie weryfikuje wieku</strong>{" "}
          użytkowników — nie wiemy więc, ile lat ma osoba zakładająca konto. Konto jest
          przeznaczone dla osób, które ukończyły 16 lat; za osobę młodszą zakłada je
          i prowadzi rodzic lub opiekun prawny. Konto założone przez osobę poniżej 16 lat
          usuwamy na zgłoszenie.
        </p>
        <p>
          W rankingu znajdują się również wyniki osób niepełnoletnich. Turniejów wyłącznie
          juniorskich nie pobieramy w ogóle, ale gdy organizator rozgrywa kategorie
          młodzieżowe razem z kategoriami dorosłych i publikuje wszystkie wyniki wspólnie,
          do serwisu trafiają także mecze dzieci. Sam wynik publikuje organizator, ale
          rating, historia startów i lista przeciwników to już produkt naszego serwisu —
          nie zasłaniamy się tu cudzą publikacją.
        </p>
        <p>
          Dlatego przyjmujemy zasadę ostrzejszą niż dla dorosłych: rodzic lub opiekun
          prawny może w każdej chwili zażądać usunięcia danych dziecka z widoków
          publicznych, pisząc na <strong>{LEGAL_CONFIG.contactEmail}</strong>, a takie
          żądanie realizujemy <strong>bez ważenia interesów</strong> — zastępujemy imię
          i nazwisko oznaczeniem pseudonimowym. Nie stosujemy tu procedury odmowy opisanej
          w punkcie 8.
        </p>
      </LegalSection>

      <LegalSection id="zmiany" number="12" title="Zmiany polityki">
        <p>
          Politykę aktualizujemy, gdy zmienia się zakres przetwarzanych danych albo
          funkcje serwisu. Aktualna wersja jest zawsze dostępna pod tym adresem, a datę
          ostatniej zmiany podajemy na początku dokumentu. O zmianach istotnych — takich
          jak nowy cel przetwarzania, nowa kategoria odbiorców albo dłuższy okres
          przechowywania — informujemy komunikatem widocznym w serwisie, publikowanym co
          najmniej 14 dni przed wejściem zmiany w życie.
        </p>
        <h3>Historia wersji</h3>
        <ul>
          <li>
            <strong>16 sierpnia 2026</strong> — doprecyzowanie po audycie zgodności:
            wskazanie współadministrowania z klubami (art. 26), pełna lista dostawców
            infrastruktury, urealnienie okresów przechowywania kopii zapasowych, jawność
            puli „szukam partnera”, opis profilowania, zasady dotyczące danych osób
            niepełnoletnich oraz odesłanie do regulaminu.
          </li>
          <li>
            <strong>14 sierpnia 2026</strong> — pierwsza wersja dokumentu.
          </li>
        </ul>
      </LegalSection>
    </LegalPage>
  );
};

export default PrivacyPolicy;
